import 'react-datepicker/dist/react-datepicker.css';
import './app-fonts';

import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { StrictMode } from 'react';
import { Provider } from '../components/ui/provider';
import { Toaster } from '../components/ui/toaster';
import AppRoutes from './app-routes';

const NON_RETRYABLE_STATUSES = [400, 401, 403, 404, 405, 409, 422];

function getErrorStatus(error: unknown): number | undefined {
  const err = error as { response?: { status?: number }; status?: number };
  return err?.response?.status ?? err?.status;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        if (status !== undefined && NON_RETRYABLE_STATUSES.includes(status)) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      networkMode: 'online',
      refetchOnMount: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        if (status !== undefined && NON_RETRYABLE_STATUSES.includes(status)) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      networkMode: 'online',
    },
  },
});

const App = () => {
  return (
    <StrictMode>
      <NuqsAdapter>
        <Provider>
          <QueryClientProvider client={queryClient}>
            <RequiredAuthProvider
              authUrl={import.meta.env.VITE_AUTH_URL}
              displayIfLoggedOut={<RedirectToLogin postLoginRedirectUrl={window.location.href} />}
              displayWhileLoading={
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  Loading...
                </div>
              }
            >
              <Toaster />
              <AppRoutes />
            </RequiredAuthProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </Provider>
      </NuqsAdapter>
    </StrictMode>
  );
};

export default App;
